import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Button, TextField, MenuItem,
  Select, FormControl, InputLabel, Chip, Card, CardContent,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, CircularProgress, Grid, RadioGroup,
  FormControlLabel, Radio, FormLabel, Divider, Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  NoteAdd as NoteAddIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { parentAPI } from '../../services/api';
import EmptyState from '../../components/EmptyState';
import MobileFilterSheet from '@components/mobile/MobileFilterSheet';
import MobileFormDialog from '@components/mobile/MobileFormDialog';

const PRIORITY_CONFIG = {
  normal:  { label: 'Normal',  color: 'default',  sx: { bgcolor: '#e0e0e0', color: '#555' } },
  high:    { label: 'High',    color: 'warning',  sx: { bgcolor: '#fff3e0', color: '#e65100' } },
  urgent:  { label: 'Urgent',  color: 'error',    sx: { bgcolor: '#ffebee', color: '#c62828' } }
};

const STATUS_CONFIG = {
  pending:      { label: 'Pending',      sx: { bgcolor: '#fff3e0', color: '#e65100' } },
  acknowledged: { label: 'Acknowledged', sx: { bgcolor: '#e3f2fd', color: '#1565c0' } },
  completed:    { label: 'Completed',    sx: { bgcolor: '#e8f5e9', color: '#2e7d32' } }
};

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = { student_id: '', note_date: today(), title: '', note_text: '', priority: 'normal' };

export default function ParentCareNotes() {
  const { user, logout } = useAuth();

  const [children, setChildren] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const [filterChild, setFilterChild] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFrom, setFilterFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [filterTo, setFilterTo] = useState(today());

  const [editDialog, setEditDialog] = useState({ open: false, note: null });
  const [editForm, setEditForm] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, note: null });
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  const fetchChildren = useCallback(async () => {
    try {
      const res = await parentAPI.getChildren();
      const kids = (res.data.children || []).filter(c => c.daycare_enrolled);
      setChildren(kids);
      if (kids.length === 1) {
        setForm(f => ({ ...f, student_id: kids[0].id }));
        setFilterChild(kids[0].id);
      }
    } catch {
      showSnack('Failed to load children', 'error');
    }
  }, []);

  const fetchNotes = useCallback(async () => {
    try {
      const params = {
        from_date: filterFrom || undefined,
        to_date: filterTo || undefined,
        student_id: filterChild || undefined
      };
      const res = await parentAPI.careNotes.list(params);
      setNotes(res.data.notes || []);
    } catch {
      showSnack('Failed to load care notes', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterFrom, filterTo, filterChild]);

  useEffect(() => { fetchChildren(); }, [fetchChildren]);
  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const validateForm = (f) => {
    const errs = {};
    if (!f.student_id) errs.student_id = 'Please select a child';
    if (!f.note_date) errs.note_date = 'Date is required';
    else if (f.note_date < today()) errs.note_date = 'Date cannot be in the past';
    if (!f.note_text.trim()) errs.note_text = 'Note text is required';
    else if (f.note_text.length > 2000) errs.note_text = 'Maximum 2000 characters';
    if (f.title && f.title.length > 200) errs.title = 'Maximum 200 characters';
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validateForm(form);
    setFormErrors(errs);
    if (Object.keys(errs).length) return;

    setSubmitting(true);
    try {
      await parentAPI.careNotes.create({
        student_id: form.student_id,
        note_date: form.note_date,
        title: form.title || undefined,
        note_text: form.note_text.trim(),
        priority: form.priority
      });
      setForm({ ...EMPTY_FORM, student_id: children.length === 1 ? children[0].id : '' });
      setFormErrors({});
      showSnack('Note sent to teacher');
      fetchNotes();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to send note', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOpen = (note) => {
    setEditForm({ title: note.title || '', note_text: note.note_text, priority: note.priority });
    setEditDialog({ open: true, note });
  };

  const handleEditSave = async () => {
    if (!editForm.note_text?.trim()) return;
    try {
      await parentAPI.careNotes.update(editDialog.note.id, {
        title: editForm.title || undefined,
        note_text: editForm.note_text.trim(),
        priority: editForm.priority
      });
      setEditDialog({ open: false, note: null });
      showSnack('Note updated');
      fetchNotes();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to update note', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await parentAPI.careNotes.remove(deleteDialog.note.id);
      setDeleteDialog({ open: false, note: null });
      showSnack('Note deleted');
      fetchNotes();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to delete note', 'error');
    }
  };

  const filteredNotes = notes.filter(n => {
    if (filterStatus !== 'all' && n.status !== filterStatus) return false;
    return true;
  });

  // Group by date
  const grouped = filteredNotes.reduce((acc, n) => {
    const d = n.note_date?.slice(0, 10) || '';
    if (!acc[d]) acc[d] = [];
    acc[d].push(n);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>Care Notes</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Send daily care instructions to your child's teacher — medication reminders, dietary needs, pickup changes, etc.
        </Typography>

        {!loading && children.length === 0 ? (
          <EmptyState
            icon={NoteAddIcon}
            title="Care Notes is a Daycare feature"
            description="None of your children are currently enrolled in daycare, so care notes aren't available."
          />
        ) : (
        <>
        {/* Create form */}
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <NoteAddIcon color="primary" /> Add a Care Note for Today
          </Typography>
          <Grid container spacing={2}>
            {children.length !== 1 && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.student_id}>
                  <InputLabel>Child</InputLabel>
                  <Select
                    value={form.student_id}
                    label="Child"
                    onChange={e => setForm(f => ({ ...f, student_id: e.target.value }))}
                  >
                    {children.map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.name} — {c.class?.name || 'No class'}</MenuItem>
                    ))}
                  </Select>
                  {formErrors.student_id && (
                    <Typography variant="caption" color="error">{formErrors.student_id}</Typography>
                  )}
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={children.length === 1 ? 6 : 6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={form.note_date}
                onChange={e => setForm(f => ({ ...f, note_date: e.target.value }))}
                inputProps={{ min: today() }}
                error={!!formErrors.note_date}
                helperText={formErrors.note_date}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title (optional)"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                error={!!formErrors.title}
                helperText={formErrors.title || `${form.title.length}/200`}
                inputProps={{ maxLength: 200 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Note *"
                placeholder="E.g., Please give the blue inhaler if she has trouble breathing during PE..."
                value={form.note_text}
                onChange={e => setForm(f => ({ ...f, note_text: e.target.value }))}
                error={!!formErrors.note_text}
                helperText={formErrors.note_text || `${form.note_text.length}/2000`}
                inputProps={{ maxLength: 2000 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>Priority</FormLabel>
                <RadioGroup row value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  <FormControlLabel value="normal" control={<Radio size="small" />} label="Normal" />
                  <FormControlLabel value="high" control={<Radio size="small" color="warning" />}
                    label={<Typography color="warning.main" fontWeight={500}>High</Typography>} />
                  <FormControlLabel value="urgent" control={<Radio size="small" color="error" />}
                    label={<Typography color="error.main" fontWeight={600}>Urgent</Typography>} />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                onClick={handleSubmit}
                disabled={submitting}
                sx={{ minWidth: 180 }}
              >
                {submitting ? 'Sending...' : 'Submit Care Note'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Filter bar */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <MobileFilterSheet label="Filter Notes" activeCount={(filterChild ? 1 : 0) + (filterStatus !== 'all' ? 1 : 0)}>
            <Grid container spacing={2} alignItems="center">
              {children.length > 1 && (
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filter by child</InputLabel>
                    <Select value={filterChild} label="Filter by child" onChange={e => setFilterChild(e.target.value)}>
                      <MenuItem value="">All children</MenuItem>
                      {children.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" label="From" type="date" value={filterFrom}
                  onChange={e => setFilterFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" label="To" type="date" value={filterTo}
                  onChange={e => setFilterTo(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm="auto">
                <Box display="flex" gap={1} flexWrap="wrap">
                  {['all', 'pending', 'acknowledged', 'completed'].map(s => (
                    <Chip
                      key={s}
                      label={s === 'all' ? 'All' : STATUS_CONFIG[s].label}
                      onClick={() => setFilterStatus(s)}
                      variant={filterStatus === s ? 'filled' : 'outlined'}
                      color={filterStatus === s ? 'primary' : 'default'}
                      size="small"
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </MobileFilterSheet>
        </Paper>

        {/* Notes list */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : sortedDates.length === 0 ? (
          filterStatus !== 'all' ? (
            <EmptyState
              icon={NoteAddIcon}
              title="No notes match the current filter"
              description="Try changing the filter above."
              variant="no-results"
            />
          ) : (
            <EmptyState
              icon={NoteAddIcon}
              title="No care notes found"
              description="Use the form above to send your first note."
            />
          )
        ) : (
          sortedDates.map(date => (
            <Box key={date} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                {new Date(date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                {grouped[date].map(note => (
                  <Card key={note.id} elevation={2} sx={{ borderRadius: 2, borderLeft: `4px solid ${note.priority === 'urgent' ? '#c62828' : note.priority === 'high' ? '#e65100' : '#9e9e9e'}` }}>
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                            {children.length > 1 && (
                              <Typography variant="caption" fontWeight={600} color="primary">
                                {note.student_name}
                              </Typography>
                            )}
                            <Chip size="small" label={PRIORITY_CONFIG[note.priority]?.label || note.priority} sx={PRIORITY_CONFIG[note.priority]?.sx} />
                            <Chip size="small" label={STATUS_CONFIG[note.status]?.label || note.status} sx={STATUS_CONFIG[note.status]?.sx} />
                          </Box>
                          {note.title && (
                            <Typography variant="subtitle2" fontWeight={600}>{note.title}</Typography>
                          )}
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {note.note_text}
                          </Typography>
                          {note.status === 'completed' && note.completion_remark && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                              <Typography variant="caption" color="success.dark" fontWeight={600}>Teacher's remark: </Typography>
                              <Typography variant="caption" color="success.dark">{note.completion_remark}</Typography>
                            </Box>
                          )}
                          {note.status === 'completed' && note.completed_at && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              <CheckCircleIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                              Completed {new Date(note.completed_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            <ScheduleIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            Sent {new Date(note.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                        {note.status === 'pending' && (
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditOpen(note)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, note })}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          ))
        )}
        </>
        )}
      </Container>

      {/* Edit dialog */}
      <MobileFormDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, note: null })}
        title="Edit Care Note"
        actions={
          <>
            <Button onClick={() => setEditDialog({ open: false, note: null })}>Cancel</Button>
            <Button variant="contained" onClick={handleEditSave} disabled={!editForm.note_text?.trim()}>Save</Button>
          </>
        }
      >
        <TextField fullWidth label="Title (optional)" value={editForm.title || ''} sx={{ mt: 1, mb: 2 }}
          onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} inputProps={{ maxLength: 200 }} />
        <TextField fullWidth multiline rows={4} label="Note *" value={editForm.note_text || ''}
          onChange={e => setEditForm(f => ({ ...f, note_text: e.target.value }))}
          helperText={`${(editForm.note_text || '').length}/2000`} inputProps={{ maxLength: 2000 }} />
        <FormControl component="fieldset" sx={{ mt: 2 }}>
          <FormLabel component="legend">Priority</FormLabel>
          <RadioGroup row value={editForm.priority || 'normal'} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}>
            <FormControlLabel value="normal" control={<Radio size="small" />} label="Normal" />
            <FormControlLabel value="high" control={<Radio size="small" color="warning" />} label="High" />
            <FormControlLabel value="urgent" control={<Radio size="small" color="error" />} label="Urgent" />
          </RadioGroup>
        </FormControl>
      </MobileFormDialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, note: null })} fullWidth maxWidth="sm">
        <DialogTitle>Delete Care Note?</DialogTitle>
        <DialogContent>
          <Typography>This note will be permanently deleted. Are you sure?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, note: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
